import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faFireFlameCurved, 
  faWifi, 
  faArrowRight, 
  faShieldHalved, 
  faPiggyBank, 
  faHeadset,
  faInfoCircle,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClipboardList,
  faUserTie,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';
import { contattiAzienda } from '../utils/data';

const Home: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto flex flex-row items-center justify-center">
          <div className="flex flex-col lg:flex-row items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12 lg:mb-0"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Confronta le offerte <span className="text-accent">luce, gas e internet</span> con un consulente Servizio Bolletta
              </h1>
              <p className="text-xl mb-4 text-white/80">
                Servizio Bolletta è un servizio indipendente di comparazione e consulenza commerciale.
                Non siamo il servizio clienti ufficiale di alcun fornitore di energia.
              </p>
              <p className="text-sm mb-8 text-white/60">
                Consulenza gratuita · Nessun obbligo di sottoscrizione · La scelta è sempre tua
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contattaci" className="btn btn-accent">
                  Richiedi Consulenza Gratuita
                </Link>
                <a href={`tel:${contattiAzienda.telefonoServizioClienti}`} className="btn btn-outline border-white text-white hover:bg-white hover:text-primary">
                  <FontAwesomeIcon icon={faHeadset} className="mr-2" /> Parla con un Consulente
                </a>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:w-1/2 lg:pl-12"
            >
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hero Disclaimer */}
      <section className="bg-gradient-to-r from-primary/5 via-white to-primary/5 border-b border-primary/10">
        <div className="container mx-auto py-5 px-4">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FontAwesomeIcon icon={faInfoCircle} className="text-primary text-lg" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-sm font-medium text-light-text mb-1">
                Servizio indipendente di comparazione
              </p>
              <p className="text-xs text-light-muted leading-relaxed">
                Servizio Bolletta è gestito da <strong className="text-light-text">GT Energie Srl</strong> (P.IVA IT02812720809). 
                Non siamo il servizio clienti ufficiale di alcun fornitore. 
                Consulenza <strong className="text-light-text">gratuita</strong> e senza obbligo di sottoscrizione.
              </p>
            </div>
            <div className="flex-shrink-0 hidden md:flex gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-200">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5 text-green-500" />
                Gratuito
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                <FontAwesomeIcon icon={faShieldHalved} className="mr-1.5 text-blue-500" />
                Indipendente
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Company Identity Block */}
      <section className="py-10 bg-white border-b border-light-border/30">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <FontAwesomeIcon icon={faShieldHalved} className="text-primary text-xl" />
              <p className="text-xs text-light-muted">Gestito da</p>
              <p className="text-sm font-semibold text-light-text">GT Energie Srl</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary text-xl" />
              <p className="text-xs text-light-muted">Sede legale</p>
              <p className="text-sm font-semibold text-light-text">Via Filippo Argelati 10, Milano</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FontAwesomeIcon icon={faPhone} className="text-primary text-xl" />
              <p className="text-xs text-light-muted">Telefono</p>
              <a href={`tel:${contattiAzienda.telefonoServizioClienti}`} className="text-sm font-semibold text-primary hover:text-secondary transition-colors">02 4005 5045</a>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FontAwesomeIcon icon={faEnvelope} className="text-primary text-xl" />
              <p className="text-xs text-light-muted">Email</p>
              <a href={`mailto:${contattiAzienda.email}`} className="text-sm font-semibold text-primary hover:text-secondary transition-colors">{contattiAzienda.email}</a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-light-background">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">Come Funziona</h2>
            <p className="text-lg text-light-muted max-w-3xl mx-auto">
              Un servizio semplice, gratuito e senza obbligo di sottoscrizione
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '1',
                icon: faClipboardList,
                title: 'Richiedi una consulenza gratuita',
                desc: 'Contattaci telefonicamente o compila il form. Il servizio è completamente gratuito e non comporta alcun impegno.',
              },
              {
                step: '2',
                icon: faUserTie,
                title: 'Confronta le offerte con un consulente',
                desc: 'Un nostro consulente ti aiuterà a confrontare le offerte disponibili sul mercato, analizzando le tue esigenze specifiche.',
              },
              {
                step: '3',
                icon: faCheckCircle,
                title: 'Scegli liberamente se attivare',
                desc: 'Se lo desideri, puoi procedere con l\'attivazione dell\'offerta più adatta. L\'attivazione è facoltativa e a tua discrezione.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * (index + 1) }}
                className="relative text-center p-8 bg-white rounded-lg shadow-sm border border-light-border/30"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                  {item.step}
                </div>
                <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-4 mt-2">
                  <FontAwesomeIcon icon={item.icon} className="text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-3 text-light-text">{item.title}</h3>
                <p className="text-light-muted text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">I Nostri Servizi di Consulenza</h2>
            <p className="text-lg text-light-muted max-w-3xl mx-auto">
              Offriamo consulenza gratuita per confrontare le offerte disponibili sul mercato
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: faLightbulb,
                title: 'Consulenza Luce',
                desc: 'Ti aiutiamo a confrontare le tariffe luce disponibili sul mercato, per trovare quella più adatta. Senza obblighi.',
                delay: 0.1,
              },
              {
                icon: faFireFlameCurved,
                title: 'Consulenza Gas',
                desc: 'Confrontiamo per te le offerte gas di diversi fornitori. La scelta finale è sempre tua, senza pressioni.',
                delay: 0.3,
              },
              {
                icon: faWifi,
                title: 'Consulenza Internet',
                desc: 'Ti presentiamo le soluzioni internet disponibili nella tua zona. Fibra, ADSL o wireless: decidi tu.',
                delay: 0.5,
              },
            ].map((service) => (
              <motion.div 
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: service.delay }}
                className="card group hover:bg-primary hover:text-white transition-all duration-300"
              >
                <div className="text-center">
                  <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6 group-hover:bg-white/20 group-hover:text-white">
                    <FontAwesomeIcon icon={service.icon} className="text-3xl" />
                  </div>
                  <h3 className="text-2xl text-black font-semibold mb-4 group-hover:text-white">{service.title}</h3>
                  <p className="mb-6 group-hover:text-white/80 text-black">{service.desc}</p>
                  <Link to="/offerte" className="inline-flex items-center text-secondary group-hover:text-accent">
                    Scopri di più <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-light-background">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">Perché Affidarti a Noi</h2>
            <p className="text-lg text-light-muted max-w-3xl mx-auto">
              Un servizio trasparente, gratuito e senza vincoli per aiutarti a fare la scelta giusta
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: faPiggyBank,
                title: 'Servizio 100% Gratuito',
                desc: 'La nostra consulenza è completamente gratuita. Confrontiamo le offerte per te senza alcun costo a tuo carico.',
                delay: 0.1,
              },
              {
                icon: faShieldHalved,
                title: 'Nessun Obbligo',
                desc: 'La sottoscrizione di qualsiasi contratto è a tua totale discrezione. Non sei obbligato ad aderire a nessuna offerta.',
                delay: 0.3,
              },
              {
                icon: faHeadset,
                title: 'Consulenza Personalizzata',
                desc: 'Un consulente dedicato analizza le tue esigenze e ti presenta le opzioni più adatte, lasciandoti libero di scegliere.',
                delay: 0.5,
              },
            ].map((item) => (
              <motion.div 
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: item.delay }}
                className="p-6 text-center"
              >
                <div className="inline-block p-4 rounded-full bg-accent/20 text-accent mb-6">
                  <FontAwesomeIcon icon={item.icon} className="text-3xl" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{item.title}</h3>
                <p className="text-light-muted">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency Banner */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="bg-white rounded-lg border border-light-border/50 p-8 flex flex-col md:flex-row items-start gap-5">
            <div className="p-3 rounded-full bg-primary/10 flex-shrink-0">
              <FontAwesomeIcon icon={faInfoCircle} className="text-primary text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-light-text mb-2">Trasparenza e libertà di scelta</h3>
              <p className="text-light-muted text-sm leading-relaxed">
                Servizio Bolletta è un servizio di <strong>consulenza gratuita e comparazione delle offerte</strong> energetiche 
                e di connettività gestito da GT Energie Srl Non siamo un fornitore diretto di energia, gas o servizi internet, 
                ma un intermediario indipendente. La nostra consulenza non comporta alcun costo per te e <strong>non implica 
                alcun obbligo di sottoscrizione</strong>. L'eventuale stipula di un contratto con un fornitore è una <strong>decisione 
                libera e autonoma del cliente</strong>. Il servizio è gratuito per l'utente; in alcuni casi Servizio Bolletta 
                potrebbe percepire una commissione commerciale dai fornitori partner in caso di attivazione di un'offerta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-12 px-4"
      >
        <div className="container mx-auto">
          <CallToAction 
            title="Vuoi confrontare le offerte?" 
            subtitle="Parla con un nostro consulente per una consulenza gratuita e senza impegno. La scelta è sempre tua."
            variant="primary"
            className="mt-8"
          />
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
